import { useCallback, useContext, useMemo, useState } from "react";
import { CoreCard, CoreCardProps, CoreModal, CoreTextBox } from "@verity/components/core";
import ProctoringPlatform from "../../../../images/settings/proctoring-platform.svg?react";
import ProctoringSettings from "../../../../images/settings/proctoring-settings.svg?react";
import ProfileName from "../../../../images/settings/profile-name.svg?react";
import i18n from "../../../../i18n";
import {
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import {
	VerityInstituteSettings,
	getDefaultProctoringOption,
	ModeOptions,
	ProctoringOptions,
} from "@verity/components";
import { Trans } from "react-i18next";
import { PortalControllerContext } from "../../../context";
import { DEFAULT_ENABLED_PROCTORING_CONFIGURATION_VALUES } from "@verity/proctoring-option";
import { FlaggedKeyword } from "@verity/database/prisma";
import { UseFetchProctoringDataReturn } from "../useFetchProctoringData";

export const InstituteProfileModal = ({
	profile,
	editProfile,
	verityInstituteSettings,
	instituteSettings,
	open,
	onClose,
	lastAddedId,
	setLastAddedId,
	applications,
	extensions,
}: {
	profile?: ProctoringOptionProfileV2;
	editProfile: (profile: ProctoringOptionProfileV2) => void;
	verityInstituteSettings: VerityInstituteSettings;
	instituteSettings: OptionRelatedInstituteSettings;
	open: boolean;
	onClose: () => void;
	lastAddedId?: number;
	setLastAddedId?: (lastAddedId: number) => void;
} & UseFetchProctoringDataReturn) => {
	const { courseSettings } = useContext(PortalControllerContext);
	const [currProfile, setCurrProfile] = useState<ProctoringOptionProfileV2>(
		profile ?? {
			proctoringOption: getDefaultProctoringOption(verityInstituteSettings),
			id: (lastAddedId ?? 0) - 1,
			name: "",
			type: "institute",
		},
	);

	const proctoringOptions = currProfile.proctoringOption;
	const isEdit = profile !== undefined;

	const title = isEdit
		? `${i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.edit")}${
				profile.name
			}${i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.setting")}`
		: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.createProfile");

	const handleUpdateProctoringOption = useCallback(
		(newOptions: Partial<ProctoringOption>) => {
			// Merge the existing full profile with the new partial changes
			const merged: ProctoringOption = {
				...proctoringOptions,
				...newOptions,
			};
			setCurrProfile({ ...currProfile, proctoringOption: merged });
		},
		[currProfile, proctoringOptions],
	);

	const commonCardProps: Partial<CoreCardProps> = useMemo(() => {
		return {
			slotProps: {
				iconWrapper: {
					style: {
						width: "20px",
						height: "20px",
					},
				},
				title: {
					fontSize: "20px",
				},
				titleWrapper: {
					style: {
						padding: "2px",
					},
				},
			},
		};
	}, []);

	return (
		<CoreModal
			open={open}
			onClose={onClose}
			title={title}
			minWidth="min(80%, 1200px)"
			minHeight="min(90%, 900px)"
			widthFixed
			heightFixed
			padding="18px 24px 12px 24px"
			buttons={[
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: isEdit
						? i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.update")
						: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.create"),
					color: "primary",
					onClick: () => {
						editProfile(currProfile);
						if (setLastAddedId && lastAddedId) {
							setLastAddedId(lastAddedId - 1);
						}
						onClose();
					},
					disabled: currProfile.name === "",
				},
			]}
		>
			<div style={{ paddingBottom: "20px", fontSize: "18px" }}>
				{isEdit
					? i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.select")
					: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.chooseName")}
			</div>
			{!isEdit && (
				<CoreCard
					{...commonCardProps}
					title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.profileName")}
					useHeader
					style={{ marginBottom: "20px" }}
					icon={<ProfileName />}
				>
					<CoreTextBox
						value={currProfile.name}
						onChange={(e) => setCurrProfile({ ...currProfile, name: e.target.value })}
						placeholder={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.enterName")}
						autoComplete="off"
						inputProps={{
							maxLength: 30,
						}}
						style={{ maxWidth: "100%", width: "400px" }}
					/>
				</CoreCard>
			)}
			<CoreCard
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.mode")}
				useHeader
				style={{ marginBottom: "20px" }}
				icon={<ProctoringPlatform />}
				slotProps={{
					...commonCardProps.slotProps,
					bodyWrapper: {
						style: {
							display: "flex",
							flexDirection: "column",
							gap: "0.625rem",
							padding: "1rem",
							paddingTop: "0",
						},
					},
				}}
			>
				<ModeOptions
					isInstitutionProfile={true}
					enforcedProfilesOnly={false}
					proctoringOption={proctoringOptions}
					setProctoringOption={handleUpdateProctoringOption}
					verityInstituteSettings={verityInstituteSettings}
					optionRelatedInstituteSettings={instituteSettings}
				/>
			</CoreCard>
			<CoreCard
				{...commonCardProps}
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.procSetting")}
				useHeader
				icon={<ProctoringSettings />}
			>
				<ProctoringOptions
					data={{
						applications,
						extensions,
						instituteKeywords: instituteSettings.flaggedKeywords as FlaggedKeyword[],
						geofences: courseSettings.geofenceLocations,
					}}
					enabledProctoringOptions={DEFAULT_ENABLED_PROCTORING_CONFIGURATION_VALUES}
					enforcedProfilesOnly={false}
					proctoringOption={proctoringOptions}
					setProctoringOption={handleUpdateProctoringOption}
					verityInstituteSettings={verityInstituteSettings}
					optionRelatedInstituteSettings={instituteSettings}
				/>
			</CoreCard>
		</CoreModal>
	);
};

export const DeleteProfileModal = ({
	profile,
	deleteProfile,
	open,
	onClose,
}: {
	profile: ProctoringOptionProfileV2;
	deleteProfile: (profile: ProctoringOptionProfileV2) => void;
	open: boolean;
	onClose: () => void;
}) => {
	return (
		<CoreModal
			title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.deleteProfile")}
			open={open}
			onClose={onClose}
			minWidth="min(60%, 550px)"
			widthFixed
			buttons={[
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.delete"),
					color: "primary",
					onClick: () => {
						deleteProfile(profile);
						onClose();
					},
				},
			]}
		>
			<Trans
				i18nKey="portal.components.settings.instituteGlobalProfile.profileModalsTSX.deleteText"
				values={{ profileName: profile.name }}
				components={{ b: <b /> }}
			/>
		</CoreModal>
	);
};
